 
import { Injectable } from '@angular/core';
import {
  Action,
  createSelector,
  Selector,
  State,
  StateContext,
} from '@ngxs/store';
import { patch } from '@ngxs/store/operators';
import {
  DataEntriesControllerService,
  DataEntry,
  UserControllerService,
} from '@osos/shared/api';
import {
  DataEntryPresenterModel,
  ManagedHttpContext,
  USER_ENTITY_ID,
  UserModel,
  Where,
} from '@osos/shared/types';
import { EMPTY, Observable, of } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import { DataEntryFactoryService } from '../../factories';
import { UserFactoryService } from '../../factories/user-factory/user-factory.service';
import { ApiUtilsService } from '../../services';
import { allowChangeUserPassword } from '../../utils';
import {
  ActivateUser,
  DeactivateUser,
  FetchCurrentUserGroups,
  FetchCurrentUserProfile,
  FetchUser,
  FetchUsersByIds,
  ResetUserPassword,
  TransitoryFetchMyOrgUsers,
} from './user.actions';

export class UserStateModel {
  user: { [key: string]: DataEntryPresenterModel };
  currentUser: DataEntryPresenterModel;
  currentUserGroups: DataEntryPresenterModel[];
  users: any[];
  presentedUsers: UserModel[];
  allowChangeUserPassword: boolean;
  transitoryMyOrgUsers: UserModel[];
}
@State<UserStateModel>({
  name: 'users',
  defaults: {
    user: {},
    currentUser: null,
    users: [],
    presentedUsers: [],
    allowChangeUserPassword: true,
    transitoryMyOrgUsers: [],
    currentUserGroups: [],
  },
})
@Injectable()
export class UserState {
  /**
   *
   */
  constructor(
    private entryService: DataEntriesControllerService,
    private apiHelper: ApiUtilsService,
    private userFac: UserFactoryService,
    private entryFactory: DataEntryFactoryService,
    private userService: UserControllerService,
  ) {}

  static user(id: string) {
    return createSelector(
      [UserState],
      (state: UserStateModel) => state.user[id],
    );
  }

  @Selector()
  static currentUser(state: UserStateModel): DataEntryPresenterModel {
    return state.currentUser;
  }

  @Selector()
  static currentUserGroups(state: UserStateModel): DataEntryPresenterModel[] {
    return state.currentUserGroups;
  }

  @Selector()
  static presentedUsers(state: UserStateModel): UserModel[] {
    return state.presentedUsers;
  }

  @Selector()
  static allowChangeUserPassword(state: UserStateModel): boolean {
    return state.allowChangeUserPassword;
  }

  @Selector()
  static transitoryMyOrgUsers(state: UserStateModel): UserModel[] {
    return state.transitoryMyOrgUsers;
  }

  @Action(FetchUser)
  fetchUser(
    { setState, getState }: StateContext<UserStateModel>,
    { userId, cached }: FetchUser,
  ) {
    const user = getState().user[userId];
    if (!!user && cached) {
      return of(user);
    }

    return this.entryService
      .findById({ entity: USER_ENTITY_ID, id: userId })
      .pipe(
        tap((res) => {
          setState(
            patch<UserStateModel>({
              user: patch({ [userId]: res }),
            }),
          );
        }),
        catchError(() => EMPTY),
      );
  }

  @Action(FetchUsersByIds)
  fetchUsersByIds(
    { setState }: StateContext<UserStateModel>,
    { ids }: FetchUsersByIds,
  ): Observable<DataEntryPresenterModel[]> {
    const where: Where<DataEntry> = {
      dataEntity: USER_ENTITY_ID,
      id: { inq: [...ids] },
    };
    return this.entryService
      .find({
        entity: USER_ENTITY_ID,
        ...this.apiHelper.prepareFilter<DataEntry>({
          where,
        }),
      })
      .pipe(
        tap((res: DataEntry[]) => {
          setState(
            patch<UserStateModel>({
              users: res,
              presentedUsers: res.map((_user) =>
                this.userFac.createPresenterModel(_user),
              ),
            }),
          );
        }),
      );
  }

  @Action(FetchCurrentUserProfile)
  fetchCurrentUser(
    { setState, getState }: StateContext<UserStateModel>,
    { cached }: FetchCurrentUserProfile,
  ): Observable<any> {
    const currentUser = getState().currentUser;
    if (!!currentUser && cached) {
      return of(currentUser);
    }
    return this.userService
      .getUserProfile({
        context: new ManagedHttpContext({
          bypassPublicMiddleware: true,
          bypassDelegationMiddleware: true,
        }),
      })
      .pipe(
        tap((res) => {
          const user = this.userFac.createPresenterModel(res);
          setState(
            patch<UserStateModel>({
              currentUser: user,
              allowChangeUserPassword: allowChangeUserPassword(user),
            }),
          );
        }),
        catchError(() => EMPTY),
      );
  }

  @Action(ResetUserPassword)
  resetUserPassword(
    ctx: StateContext<UserStateModel>,
    { userId }: ResetUserPassword,
  ) {
    return this.userService.resetUserPasswordBySystem({
      userId: userId,
    });
  }
  @Action(ActivateUser)
  activateUser(ctx: StateContext<UserStateModel>, { userId }: ActivateUser) {
    return this.userService.activateUser({
      userId: userId,
    });
  }
  @Action(DeactivateUser)
  deactivateUser(
    ctx: StateContext<UserStateModel>,
    { userId }: DeactivateUser,
  ) {
    return this.userService.deactivateUser({
      userId: userId,
    });
  }

  @Action(TransitoryFetchMyOrgUsers)
  fetchMyOrgUsers({ setState }: StateContext<UserStateModel>) {
    return this.userService.getUserOrgUnitUsers().pipe(
      tap((users: DataEntry[]) => {
        const presentedUsers = users.map((user) =>
          this.userFac.createPresenterModel(user),
        );
        setState(
          patch<UserStateModel>({
            transitoryMyOrgUsers: presentedUsers,
          }),
        );
      }),
    );
  }

  @Action(FetchCurrentUserGroups)
  fetchCurrentUserGroups(
    { setState, getState }: StateContext<UserStateModel>,
    { cached }: FetchCurrentUserGroups,
  ): Observable<any> {
    const currentUserGroups = getState().currentUserGroups;
    if (!!currentUserGroups?.length && cached) {
      return of(currentUserGroups);
    }
    return this.userService
      .getMyGroups({
        context: new ManagedHttpContext({ bypassPublicMiddleware: true }),
      })
      .pipe(
        map((res) =>
          res.map((group) => this.entryFactory.createPresenterModel(group)),
        ),
        tap((res) => {
          setState(
            patch<UserStateModel>({
              currentUserGroups: res,
            }),
          );
        }),
        catchError(() => EMPTY),
      );
  }
}
